import { endOfDay, formatISO, startOfDay } from 'date-fns';

import { DEFAULT_METRICS_FILTER_RANGE } from '@/hooks/use-metrics-filter-params';
import {
  formatCalendarDateParam,
  lastDaysRange,
  toDate,
} from '@/utils/date-util';

import type { DateRangePreset } from '@/components/ui/date-range-picker';
import { LIFETIME } from '@/components/ui/date-range-picker';
import type {
  SourcingHiringGoal,
  SourcingMetricsFilterRange,
  SourcingOpeningFilterDuration,
} from 'api-clients/monolith';
import type { DateRange } from 'react-day-picker';

/** No client-side bounds for lifetime; dates come from the API when available. */
export const EMPTY_METRICS_DATE_RANGE: DateRange = {
  from: undefined,
  to: undefined,
};

type HiringGoalMetricsSource = Pick<
  SourcingHiringGoal,
  'start_date' | 'hire_by' | 'external_id'
>;

function computeFilterRangeDates(
  range: Exclude<SourcingMetricsFilterRange, typeof LIFETIME>,
  localDays: boolean,
  today?: Date,
): DateRange {
  return lastDaysRange(Number(range), localDays, today);
}

/**
 * The first day of a range: a calendar date the API reads at the opening, or the
 * start of the day in the browser timezone (ISO 8601).
 */
function formatMetricsStartDateParam(date: Date, localDays: boolean): string {
  return localDays
    ? formatCalendarDateParam(date)
    : formatISO(startOfDay(date));
}

/**
 * The last day of a range: a calendar date the API reads at the opening, or the
 * end of the day in the browser timezone (ISO 8601).
 */
function formatMetricsEndDateParam(date: Date, localDays: boolean): string {
  return localDays ? formatCalendarDateParam(date) : formatISO(endOfDay(date));
}

/** Resolved bounds from the openings API `filter_duration` payload. */
export function getFilterDurationDateRange(
  filterDuration?: SourcingOpeningFilterDuration | null,
): DateRange | undefined {
  if (!filterDuration?.start_date) return undefined;

  const from = toDate(filterDuration.start_date) ?? undefined;
  const to = toDate(filterDuration?.end_date ?? new Date()) ?? undefined;

  return { from, to };
}

function isLifetimeMetricsContext(
  preset: DateRangePreset,
  filterRange?: SourcingMetricsFilterRange,
): boolean {
  return preset === LIFETIME || filterRange === LIFETIME;
}

export type MetricsFilterApply =
  | { type: 'filter_range'; filterRange: SourcingMetricsFilterRange }
  | { type: 'custom'; startDate: string; endDate: string };

/**
 * Mirrors backend defaults when metrics URL params are omitted:
 * hiring-goal dates when a goal exists, otherwise org `default_metrics_period`.
 */
export function getBackendMetricsDateRangeState(
  defaultMetricsPeriod: string | null | undefined,
  hiringGoal?: HiringGoalMetricsSource | null,
  localDays = false,
  /** Today at the opening; the browser's today when omitted. */
  today?: Date,
): {
  range: DateRange;
  preset: DateRangePreset;
  /** Null for lifetime: omit URL params and let the API apply org lifetime defaults. */
  urlApply: MetricsFilterApply | null;
} {
  const hasActiveHiringGoal = Boolean(hiringGoal?.external_id);

  if (hasActiveHiringGoal && hiringGoal) {
    const from = toDate(hiringGoal.start_date);
    const to = hiringGoal.hire_by ? toDate(hiringGoal.hire_by) : new Date();

    if (from && to) {
      return {
        preset: 'custom',
        range: { from, to },
        urlApply: {
          type: 'custom',
          startDate: formatMetricsStartDateParam(from, localDays),
          endDate: formatMetricsEndDateParam(to, localDays),
        },
      };
    }
  }

  if (defaultMetricsPeriod === null) {
    return {
      preset: LIFETIME,
      range: EMPTY_METRICS_DATE_RANGE,
      urlApply: null,
    };
  }

  const filterRange = defaultMetricsPeriod as SourcingMetricsFilterRange;

  if (filterRange) {
    if (filterRange === LIFETIME) {
      return {
        preset: LIFETIME,
        range: EMPTY_METRICS_DATE_RANGE,
        urlApply: null,
      };
    }
    return {
      preset: filterRange,
      range: computeFilterRangeDates(filterRange, localDays, today),
      urlApply: { type: 'filter_range', filterRange },
    };
  }

  const days = Number(defaultMetricsPeriod ?? '30');
  const { from, to } = lastDaysRange(days, localDays, today);

  return {
    preset: 'custom',
    range: { from, to },
    urlApply: {
      type: 'custom',
      startDate: formatMetricsStartDateParam(from, localDays),
      endDate: formatMetricsEndDateParam(to, localDays),
    },
  };
}

function getMetricsDateRangeState(
  startDate: string | undefined,
  endDate: string | undefined,
  filterRange: SourcingMetricsFilterRange | undefined,
  localDays: boolean,
  today?: Date,
): { range: DateRange; preset: DateRangePreset } {
  if (startDate && endDate) {
    return {
      preset: 'custom',
      range: {
        from: toDate(startDate) ?? undefined,
        to: toDate(endDate) ?? undefined,
      },
    };
  }

  const effectiveRange = filterRange ?? DEFAULT_METRICS_FILTER_RANGE;

  if (effectiveRange === LIFETIME) {
    return { preset: LIFETIME, range: EMPTY_METRICS_DATE_RANGE };
  }

  return {
    preset: effectiveRange,
    range: computeFilterRangeDates(effectiveRange, localDays, today),
  };
}

/**
 * Query params for metrics APIs.
 * Rolling `filter_range` presets omit end_date so the API mirrors openings:
 * start = now - N days, end open (through campaign end for duration/target;
 * spend fetch still caps at now server-side).
 */
export function resolveMetricsApiDateParams({
  startDate,
  endDate,
  filterRange,
  localDays = false,
}: {
  startDate?: string;
  endDate?: string;
  filterRange?: SourcingMetricsFilterRange;
  localDays?: boolean;
}): {
  startDate?: string;
  endDate?: string;
  filterRange?: SourcingMetricsFilterRange;
} {
  if (startDate || endDate || !filterRange || filterRange === LIFETIME) {
    return { startDate, endDate, filterRange };
  }

  const { from } = computeFilterRangeDates(filterRange, localDays);
  if (!from) {
    return { filterRange };
  }

  return {
    startDate: formatMetricsStartDateParam(from, localDays),
    endDate: undefined,
    filterRange,
  };
}

/**
 * Resolves picker display state. Lifetime never uses client-computed bounds.
 */
export function resolveMetricsDisplayState({
  startDate,
  endDate,
  filterRange,
  defaultMetricsPeriod,
  filterDuration,
  localDays = false,
  today,
}: {
  startDate?: string;
  endDate?: string;
  filterRange?: SourcingMetricsFilterRange;
  defaultMetricsPeriod?: string | null;
  filterDuration?: SourcingOpeningFilterDuration | null;
  localDays?: boolean;
  /** Today at the opening; the browser's today when omitted. */
  today?: Date;
}): { range: DateRange; preset: DateRangePreset } {
  const hasUrlMetricsFilter =
    Boolean(startDate && endDate) || Boolean(filterRange);

  const filterDurationRange = getFilterDurationDateRange(filterDuration);

  if (hasUrlMetricsFilter) {
    const urlState = getMetricsDateRangeState(
      startDate,
      endDate,
      filterRange,
      localDays,
      today,
    );

    if (isLifetimeMetricsContext(urlState.preset, filterRange)) {
      return {
        preset: LIFETIME,
        range: filterDurationRange ?? EMPTY_METRICS_DATE_RANGE,
      };
    }

    return urlState;
  }

  const backendDefaults = getBackendMetricsDateRangeState(
    defaultMetricsPeriod,
    null,
    localDays,
    today,
  );

  if (backendDefaults.preset === LIFETIME) {
    return {
      preset: LIFETIME,
      range: filterDurationRange ?? EMPTY_METRICS_DATE_RANGE,
    };
  }

  return {
    preset: backendDefaults.preset,
    range: backendDefaults.range,
  };
}

export function getMetricsFilterApply(
  preset: DateRangePreset | undefined,
  range: DateRange,
  localDays = false,
): MetricsFilterApply | null {
  if (!preset) return null;

  if (preset !== 'custom') {
    return { type: 'filter_range', filterRange: preset };
  }

  if (range.from && range.to) {
    return {
      type: 'custom',
      startDate: formatMetricsStartDateParam(range.from, localDays),
      endDate: formatMetricsEndDateParam(range.to, localDays),
    };
  }

  return null;
}

export function applyMetricsFilterToUrl(
  apply: MetricsFilterApply,
  handlers: {
    applyFilterRange: (range: SourcingMetricsFilterRange) => void;
    setMetricsFilter: (update: {
      startDate: string;
      endDate: string;
      filterRange: undefined;
    }) => void;
  },
) {
  if (apply.type === 'filter_range') {
    handlers.applyFilterRange(apply.filterRange);
    return;
  }

  handlers.setMetricsFilter({
    startDate: apply.startDate,
    endDate: apply.endDate,
    filterRange: undefined,
  });
}
